const GOOGLE_TRANSLATE_COOKIE = 'googtrans';
const GOOGLE_TRANSLATE_SCRIPT_ID = 'google-translate-script';
const GOOGLE_TRANSLATE_CONTAINER_ID = 'google_translate_element_vibemax';
const LANGUAGE_STORAGE_KEY = 'vibemax_language';

class AutoTranslateSelector extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.auto-translate-selector__trigger');
    this.valueElement = this.querySelector('.auto-translate-selector__value');
    this.menu = this.querySelector('.auto-translate-selector__menu');
    this.options = [...this.querySelectorAll('[data-language-code]')];
    if (!(this.trigger instanceof HTMLButtonElement) || !(this.menu instanceof HTMLElement)) return;

    this.syncDefaultLanguage();
    this.setActiveLanguage(this.currentLanguage);
    this.trigger.addEventListener('click', this.toggleMenu);
    this.options.forEach((option) => option.addEventListener('click', this.onLanguageSelect));
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    this.loadGoogleTranslate();
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.toggleMenu);
    this.options?.forEach((option) => option.removeEventListener('click', this.onLanguageSelect));
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  get currentLanguage() {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage) return savedLanguage;

    const cookie = this.getCookie(GOOGLE_TRANSLATE_COOKIE);
    const targetLanguage = cookie?.split('/').filter(Boolean).pop();

    if (targetLanguage && targetLanguage !== 'auto') return targetLanguage;

    return 'en';
  }

  toggleMenu = () => {
    const isOpen = this.hasAttribute('open');
    this.setMenuOpen(!isOpen);
  };

  onDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.setMenuOpen(false);
  };

  onDocumentKeydown = (event) => {
    if (event.key === 'Escape') this.setMenuOpen(false);
  };

  onLanguageSelect = (event) => {
    const option = event.currentTarget;
    if (!(option instanceof HTMLElement)) return;

    const language = option.dataset.languageCode;
    if (!language) return;

    if (language === 'en') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, 'en');
      this.clearTranslateCookie();
    } else {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      this.setTranslateCookie(`/auto/${language}`);
    }

    window.location.reload();
  };

  syncDefaultLanguage() {
    if (localStorage.getItem(LANGUAGE_STORAGE_KEY)) return;

    this.clearTranslateCookie();
  }

  setMenuOpen(isOpen) {
    this.toggleAttribute('open', isOpen);
    this.trigger?.setAttribute('aria-expanded', String(isOpen));
    if (this.menu) this.menu.hidden = !isOpen;
  }

  setActiveLanguage(language) {
    const activeLanguage = language || 'en';
    const activeOption = this.options.find((option) => option.dataset.languageCode === activeLanguage);
    const shortLabel = activeLanguage === 'zh-CN' ? '中文' : activeLanguage.toUpperCase();

    if (this.valueElement) this.valueElement.textContent = shortLabel;
    this.options.forEach((option) => {
      option.toggleAttribute('aria-current', option === activeOption);
    });
  }

  loadGoogleTranslate() {
    this.ensureTranslateContainer();

    window.googleTranslateElementInit = () => {
      if (!window.google?.translate?.TranslateElement) return;

      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'auto',
          includedLanguages: 'th,en,zh-CN,id,ms',
          autoDisplay: false,
        },
        GOOGLE_TRANSLATE_CONTAINER_ID
      );
    };

    if (document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
    script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    document.head.appendChild(script);
  }

  ensureTranslateContainer() {
    if (document.getElementById(GOOGLE_TRANSLATE_CONTAINER_ID)) return;

    const container =
      this.querySelector('[data-google-translate-container]') || document.createElement('div');
    container.id = GOOGLE_TRANSLATE_CONTAINER_ID;
    container.hidden = true;

    if (!container.parentElement) document.body.appendChild(container);
  }

  setTranslateCookie(value) {
    const maxAge = 60 * 60 * 24 * 365;
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=${value};path=/;max-age=${maxAge}`;
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=${value};path=/;domain=.${window.location.hostname};max-age=${maxAge}`;
  }

  clearTranslateCookie() {
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=;path=/;max-age=0`;
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=;path=/;domain=.${window.location.hostname};max-age=0`;
  }

  getCookie(name) {
    return document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))
      ?.split('=')
      .slice(1)
      .join('=');
  }
}

if (!customElements.get('auto-translate-selector')) {
  customElements.define('auto-translate-selector', AutoTranslateSelector);
}
